import { useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '../store/authStore'
import toast from 'react-hot-toast'

const IDLE_TIMEOUT = 30 * 60 * 1000 // 30 minutes
const WARNING_BEFORE = 5 * 60 * 1000 // warn 5 minutes before

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const

export function useSessionTimeout() {
  const { isAuthenticated, adminLogout } = useAuthStore()
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningShown = useRef(false)

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (warningRef.current) clearTimeout(warningRef.current)
    warningShown.current = false
  }, [])

  const resetTimer = useCallback(() => {
    clearTimers()
    if (!isAuthenticated) return

    // Warning timer
    warningRef.current = setTimeout(() => {
      if (!warningShown.current) {
        warningShown.current = true
        toast('Your session will expire in 5 minutes due to inactivity.', {
          icon: '⏳',
          duration: 10000,
        })
      }
    }, IDLE_TIMEOUT - WARNING_BEFORE)

    // Logout timer
    timeoutRef.current = setTimeout(() => {
      toast.error('Session expired due to inactivity.')
      adminLogout()
    }, IDLE_TIMEOUT)
  }, [isAuthenticated, adminLogout, clearTimers])

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers()
      return
    }

    resetTimer()

    const handleActivity = () => {
      if (warningShown.current) {
        warningShown.current = false
      }
      resetTimer()
    }

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true })
    }

    return () => {
      clearTimers()
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity)
      }
    }
  }, [isAuthenticated, resetTimer, clearTimers])
}
